import Vue from "vue";

export interface Vendor {
  id: Number;
  vendor: "AUTHORIZE_NET" | "GMAIL" | "GOOGLECALENDAR";
  displayName: string;
  authorizationEndpoint: string;
  providerAuthorizationUrl: string;
  settings: Map<String, String>;
}

export interface ConnectionTestResult {
  status: string;
  message: string;
}

class OAuthService {
  // Fetch supported vendors
  public async getSupportedVendors(): Promise<Vendor[]> {
    try {
      const response = await Vue.axios.get<Vendor[]>("/api/v1/oauth2/vendors");
      return response.data;
    } catch (error) {
      console.error("Failed to fetch supported vendors", error);
      throw error; // Propagate the error for handling by the caller
    }
  }

  // Revoke authorization
  public async revokeAuthorization(
    vendor: "AUTHORIZE_NET" | "GMAIL" | "GOOGLECALENDAR",
    centerId?: number,
  ): Promise<void> {
    try {
      const params: any = {};
      if (centerId) {
        params.centerId = centerId;
      }
      await Vue.axios.delete("/api/v1/oauth2/revoke/" + vendor, { params });
    } catch (error) {
      console.error(`Failed to revoke authorization for ${vendor}`, error);
      throw error; // Propagate the error for handling by the caller
    }
  }

  // Test connection for a given vendor.
  public async testConnection(
    vendor: "AUTHORIZE_NET" | "GMAIL" | "GOOGLECALENDAR",
    centerId?: number,
  ): Promise<ConnectionTestResult> {
    try {
      const params: any = {};
      if (centerId) {
        params.centerId = centerId;
      }
      // Use the generic type parameter to indicate that the response data conforms to ConnectionTestResult.
      const response = await Vue.axios.get<ConnectionTestResult>(
        `/api/v1/oauth2/testConnection/${vendor}`,
        {
          params,
        },
      );
      return response.data;
    } catch (error) {
      console.error(`Test connection failed for ${vendor}`, error);
      throw error; // Propagate the error for handling by the caller
    }
  }

  // Update settings for a given OAuth credential using its ID.
  public async updateSettings(
    id: number,
    settings: Record<string, any>,
  ): Promise<any> {
    try {
      const response = await Vue.axios.put(
        `/api/v1/oauth2/settings/${id}`,
        settings,
      );
      return response.data;
    } catch (error) {
      console.error("Failed to update settings", error);
      throw error; // Propagate the error for handling by the caller
    }
  }

  public async getWebhookUrl(accountId: number): Promise<string> {
    try {
      const response = await Vue.axios.get<string>(
        "/api/webhook/authorize/webhook-url/" + accountId,
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch webhook URL", error);
      throw error; // Propagate the error for handling by the caller
    }
  }

  public async getGmailAliases(accountId: number): Promise<string[]> {
    try {
      const response = await Vue.axios.get<string[]>("/api/v1/oauth2/gmailAliases", {
        params: { accountId },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to fetch Gmail aliases", error);
      throw error;
    }
  }
}

export default new OAuthService();
